import { useEffect, useState } from 'react';

import { de } from '../../i18n/de';
import { ActivateButton } from '../../components/ActivateButton';
import { ContentBand } from '../../components/ContentBand';
import { categoryRepo, phraseRepo } from '../../data/repositories';
import { SAETZE_COLUMNS, shownInSaetze } from '../../core/authoring/capacity';
import { speechService } from '../../core/speech/SpeechService';
import { useProfileStore } from '../../core/profile/ProfileStore';
import { useUiStore } from '../../app/uiStore';
import type { Category, Phrase } from '../../core/types';

/**
 * The phrase bank: categories, then phrases, then spoken.
 *
 * **Two steps rather than one screen**, because no category holds more than eleven phrases, so every
 * grid fits without scrolling or paging. Scrolling is a visual-search cost we cannot measure for
 * this person, and hiding a phrase below a fold is worse than asking for one more tap. That eleven
 * is enforced rather than assumed: `core/authoring/capacity.ts` owns the number, this grid renders
 * from it, and care mode refuses from it (`AUT-08`) — though the import still does not (C85).
 *
 * **The Notfall category is not shown here.** Those five phrases have their own surface, which also
 * protects them from editing — two routes to the same five sentences would be search cost with no
 * benefit.
 *
 * Tap speaks immediately (§4.2). `confirmOnPhraseTap` inserts a confirmation step and is off by
 * default; it exists for the day tremor starts causing misfires.
 */

export function Saetze(): React.JSX.Element {
  const [categories, setCategories] = useState<Category[]>([]);
  const [phrases, setPhrases] = useState<Phrase[]>([]);
  const [pendingConfirm, setPendingConfirm] = useState<Phrase | null>(null);
  const openCategoryId = useUiStore((s) => s.openCategoryId);
  const openCategory = useUiStore((s) => s.openCategory);
  const confirmOnTap = useProfileStore((s) => s.settings.confirmOnPhraseTap);

  useEffect(() => {
    void categoryRepo.all().then((all) => setCategories(shownInSaetze(all)));
  }, []);

  useEffect(() => {
    if (!openCategoryId) {
      setPhrases([]);
      return;
    }
    void phraseRepo.byCategory(openCategoryId).then(setPhrases);
  }, [openCategoryId]);

  const speak = (phrase: Phrase): void => {
    void speechService.speak({ text: phrase.text, source: 'phrase', isPhrase: true });
    void phraseRepo.recordUse(phrase.id);
    setPendingConfirm(null);
  };

  const onPhraseTap = (phrase: Phrase): void => {
    if (confirmOnTap && pendingConfirm?.id !== phrase.id) {
      setPendingConfirm(phrase);
      return;
    }
    speak(phrase);
  };

  if (!openCategoryId) {
    return (
      <SurfaceGrid testId="category-grid">
        {categories.map((category) => (
          <Tile
            key={category.id}
            id={`category:${category.id}`}
            icon={category.icon}
            label={category.name}
            accent={category.colour}
            onPress={() => openCategory(category.id)}
          />
        ))}
      </SurfaceGrid>
    );
  }

  const category = categories.find((c) => c.id === openCategoryId);

  return (
    <SurfaceGrid testId="phrase-grid">
      <Tile id="category:back" icon="◀" label={de.back} onPress={() => openCategory(null)} />
      {phrases.map((phrase) => (
        <Tile
          key={phrase.id}
          id={`phrase:${phrase.id}`}
          icon={phrase.icon}
          label={phrase.shortLabel ?? phrase.text}
          accent={category?.colour}
          pinned={phrase.pinned}
          confirming={pendingConfirm?.id === phrase.id}
          onPress={() => onPhraseTap(phrase)}
        />
      ))}
    </SurfaceGrid>
  );
}

/**
 * The grid inside the content band. Twelve slots covers every category (the largest has eleven
 * phrases plus a back tile) with no scrolling anywhere in the surface. The column count comes from
 * `capacity.ts` because the same number is what care mode refuses on (`AUT-08`), and it is four
 * rather than three because a fourth *row* does not clear the 16 mm floor once the audio banner has
 * taken its height (`GEO-23`, ADR-044).
 */
function SurfaceGrid({
  children,
  testId,
}: {
  children: React.ReactNode;
  testId: string;
}): React.JSX.Element {
  return (
    <ContentBand grow>
      <div
        data-testid={testId}
        style={{
          display: 'grid',
          gridTemplateColumns: `repeat(${SAETZE_COLUMNS}, 1fr)`,
          gridAutoRows: '1fr',
          gap: 'var(--key-gap)',
          height: '100%',
          paddingBlock: 'var(--band-gap)',
        }}
      >
        {children}
      </div>
    </ContentBand>
  );
}

function Tile({
  id,
  icon,
  label,
  accent,
  pinned = false,
  confirming = false,
  onPress,
}: {
  id: string;
  icon?: string | undefined;
  label: string;
  accent?: string | undefined;
  pinned?: boolean;
  confirming?: boolean;
  onPress: () => void;
}): React.JSX.Element {
  return (
    <ActivateButton
      id={id}
      onActivate={onPress}
      label={label}
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 4,
        minHeight: 'var(--min-target)',
        padding: 8,
        background: confirming ? 'var(--accent)' : 'var(--key-bg)',
        color: confirming ? 'var(--accent-fg)' : 'var(--key-fg)',
        // The category colour is a border, not a fill: colour must never be the only signal
        // (§5.2), and the icon plus the text carry the meaning on their own.
        //
        // Truthiness rather than `??`, and that is the whole of a defect (ADR-045): `??` does not fire for
        // the empty string, `4px solid ` is an invalid shorthand, the declaration is dropped, and
        // `base.css`'s `button { border: none }` wins — so the tile would lose its border entirely rather
        // than fall back to anything. An absent colour means the theme's (`AUT-11`).
        border: `4px solid ${accent || 'var(--key-border)'}`,
        borderStyle: pinned ? 'double' : 'solid',
        borderRadius: 'var(--radius)',
      }}
    >
      {icon ? <span style={{ fontSize: 'var(--font-button)' }}>{icon}</span> : null}
      <span style={{ fontSize: 'var(--font-button)', textAlign: 'center', overflowWrap: 'anywhere' }}>
        {confirming ? de.confirm : label}
      </span>
    </ActivateButton>
  );
}
